'use client'
import {
  ContentfulMedia_default,
  ThemeButton_default
} from "./chunk-XIZE6A22.mjs";
import {
  useFilterState
} from "./chunk-TOSXVIFC.mjs";
import {
  useGetOrganizerList
} from "./chunk-AI3DVZJL.mjs";
import {
  useGetEventsList
} from "./chunk-I4GWSKHL.mjs";
import {
  useGetCategories
} from "./chunk-HKGNX5TB.mjs";
import {
  __objRest,
  __spreadProps,
  __spreadValues
} from "./chunk-BTG5EALA.mjs";

// src/components/filters/Filters.tsx
import { Fragment, useRef } from "react";
import { Divider as Divider2, Typography as Typography4 } from "@wbk/ui";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation as useTranslation6 } from "@wbk/config";
import { FilterIcon } from "@wbk/svg";

// src/components/ui/extensions/ThemeResponsiveModal.tsx
import { twMerge } from "tailwind-merge";
import { ResponsiveModal as BaseResponsiveModal } from "@wbk/ui";
import { jsx } from "react/jsx-runtime";
var ThemeResponsiveModal = (_a) => {
  var _b = _a, { className } = _b, props = __objRest(_b, ["className"]);
  return /* @__PURE__ */ jsx(
    BaseResponsiveModal,
    __spreadProps(__spreadValues({}, props), {
      className: twMerge(
        "bg-secondary/60 rounded-XL backdrop-blur-xl [&_div]:bg-transparent",
        className
      )
    })
  );
};
var ThemeResponsiveModal_default = ThemeResponsiveModal;

// src/components/filters/Price.tsx
import { useState } from "react";
import { Slider, Switch, Typography } from "@wbk/ui";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "@wbk/config";
import { CurrencyPriceLabel } from "@wbk/currency";
import { jsx as jsx2, jsxs } from "react/jsx-runtime";
var PriceFilter = () => {
  var _a;
  const { watch, setValue } = useFormContext();
  const { t } = useTranslation("calendar_explore");
  const { data: sortedEvents } = useGetEventsList(
    {
      order: "price_DESC",
      limit: 1
    },
    { staleTime: Infinity }
  );
  const [fromState, toState] = watch(["minPrice", "maxPrice"]);
  const hightestPrice = ((_a = sortedEvents == null ? void 0 : sortedEvents.items[0]) == null ? void 0 : _a.price) || 0;
  const from = Number(fromState) || 0;
  const to = Number(toState);
  const safeTo = typeof toState === "undefined" ? hightestPrice : to;
  const [free, setFree] = useState(from === 0 && to === 0);
  return /* @__PURE__ */ jsxs("div", { className: "space-y-4", children: [
    /* @__PURE__ */ jsx2(Typography, { strong: true, variant: "label-L", children: t("calendar_explore:price") }),
    /* @__PURE__ */ jsxs("div", { className: "space-y-8", children: [
      /* @__PURE__ */ jsxs("div", { className: "space-y-4", children: [
        /* @__PURE__ */ jsx2(
          Slider,
          {
            defaultValue: free ? [0, 0] : [from, safeTo],
            className: "[&_[role=slider]]:bg-primary [&_[role=slider]]:rounded-full",
            onPointerDown: () => {
              if (free) {
                setFree(false);
                setValue("minPrice", void 0);
                setValue("maxPrice", void 0);
              }
            },
            onValueCommit: ([from2, to2]) => {
              const reset = from2 === 0 && to2 === hightestPrice;
              const newFrom = reset ? void 0 : from2;
              const newTo = reset ? void 0 : to2;
              setValue("minPrice", newFrom == null ? void 0 : newFrom.toString());
              setValue("maxPrice", newTo == null ? void 0 : newTo.toString());
            },
            minStepsBetweenThumbs: 2,
            step: hightestPrice * 0.05,
            min: 0,
            max: hightestPrice,
            disabled: from === 0 && to === 0
          },
          `${hightestPrice}_${free}`
        ),
        /* @__PURE__ */ jsxs("div", { className: "text-primary flex items-center gap-2", children: [
          /* @__PURE__ */ jsx2(CurrencyPriceLabel, { originalPrice: from }),
          " ",
          " - ",
          /* @__PURE__ */ jsx2(CurrencyPriceLabel, { originalPrice: to === 0 ? 0 : to || hightestPrice })
        ] })
      ] }),
      /* @__PURE__ */ jsx2(
        Switch,
        {
          onChange: (checked) => {
            setFree(checked);
            setValue("minPrice", "0");
            setValue("maxPrice", checked ? "0" : hightestPrice == null ? void 0 : hightestPrice.toString());
          },
          size: "M",
          checked: free,
          groupClassName: "[&_div]:justify-between [&_div]:flex-row-reverse",
          label: /* @__PURE__ */ jsx2(Typography, { variant: "label-L", strong: true, children: t("calendar_explore:free_events_only") }),
          hideErrorSpace: true
        }
      )
    ] })
  ] });
};
var Price_default = PriceFilter;

// src/components/filters/Date.tsx
import { Controller, useFormContext as useFormContext2 } from "react-hook-form";
import { DayPicker, ToggleGroup, Typography as Typography2 } from "@wbk/ui";
import { formatDate } from "@wbk/utils";
import { AnimatePresence, motion } from "motion/react";
import { useRouter, useTranslation as useTranslation2 } from "@wbk/config";
import { jsx as jsx3, jsxs as jsxs2 } from "react/jsx-runtime";
var dateOptions = [
  {
    text: "today",
    value: "today",
    id: "today"
  },
  {
    text: "tomorrow",
    value: "tomorrow",
    id: "tomorrow"
  },
  {
    text: "thisweek",
    value: "thisweek",
    id: "thisweek"
  },
  {
    text: "thismonth",
    value: "thismonth",
    id: "thismonth"
  },
  {
    text: "custom_date",
    value: "custom",
    id: "custom"
  }
];
var DateFilter = () => {
  const { t } = useTranslation2("calendar_explore");
  const { control, setValue, watch } = useFormContext2();
  const { locale } = useRouter();
  const [from, to] = watch(["from", "to"]);
  return /* @__PURE__ */ jsx3(
    Controller,
    {
      control,
      name: "date",
      render: ({ field: { onChange, value } }) => {
        const isCustom = value === "custom";
        return /* @__PURE__ */ jsxs2("div", { className: "space-y-2", children: [
          /* @__PURE__ */ jsx3(Typography2, { strong: true, variant: "label-L", children: t("calendar_explore:date") }),
          /* @__PURE__ */ jsx3(
            ToggleGroup,
            {
              showClose: true,
              type: "single",
              className: "rounded-L flex-initial border-2",
              value,
              onChange: (newValue) => {
                if (newValue !== "custom") {
                  setValue("from", void 0);
                  setValue("to", void 0);
                }
                onChange(newValue);
              },
              options: dateOptions.map((option) => ({
                text: t(`calendar_explore:${option.text}`),
                value: option.value
              })),
              btnProps: { theme: "secondary", shape: "solid", phill: true },
              activeBtnProps: {
                theme: "secondary",
                phill: true,
                className: "bg-body-light border-tertiary hover:bg-body-light hover:bg-secondary-400"
              }
            }
          ),
          /* @__PURE__ */ jsx3(AnimatePresence, { children: isCustom && /* @__PURE__ */ jsx3(
            motion.div,
            {
              initial: { height: 0 },
              animate: { height: isCustom ? "auto" : 0 },
              exit: { height: 0 },
              className: "overflow-hidden",
              children: /* @__PURE__ */ jsx3(
                DayPicker,
                {
                  className: "bg-body-level-1 w-full rounded-lg p-2",
                  lang: locale,
                  mode: "range",
                  fromDate: /* @__PURE__ */ new Date(),
                  defaultMonth: from,
                  selected: { from, to },
                  onSelect: (value2) => {
                    setValue("from", value2 == null ? void 0 : value2.from);
                    setValue("to", value2 == null ? void 0 : value2.to);
                  },
                  components: {
                    CaptionLabel: ({ displayMonth }) => {
                      return /* @__PURE__ */ jsx3(Typography2, { className: "px-4", children: formatDate(displayMonth, locale, { month: "long", year: "numeric" }) });
                    }
                  }
                }
              )
            }
          ) })
        ] });
      }
    }
  );
};
var Date_default = DateFilter;

// src/components/filters/Organizers.tsx
import { useFormContext as useFormContext3 } from "react-hook-form";
import { useRouter as useRouter2, useTranslation as useTranslation4 } from "@wbk/config";

// src/components/filters/CheckboxList.tsx
import { useState as useState2 } from "react";
import { Button, Divider, Typography as Typography3 } from "@wbk/ui";
import { motion as motion2 } from "motion/react";
import { useTranslation as useTranslation3 } from "@wbk/config";
import { CloseIcon } from "@wbk/svg";
import { jsx as jsx4, jsxs as jsxs3 } from "react/jsx-runtime";
var FilterCheckboxList = ({ title, list, onChange, loading, initialVisible = 5 }) => {
  const [showAll, setShowAll] = useState2(false);
  const { t } = useTranslation3("calendar_common");
  const maxVisible = Math.min((list == null ? void 0 : list.length) || 0, initialVisible);
  if (loading) {
    return /* @__PURE__ */ jsxs3("div", { className: "space-y-4", children: [
      /* @__PURE__ */ jsx4(Typography3, { as: "h2", variant: "body-L", children: title }),
      /* @__PURE__ */ jsx4("div", { className: "space-y-2 overflow-hidden px-0.5", children: [1, 2, 3].map((i) => /* @__PURE__ */ jsx4("div", { className: "bg-body-level-1 h-7 animate-pulse rounded-md" }, i)) })
    ] });
  }
  if (!(list == null ? void 0 : list.length)) {
    return null;
  }
  return /* @__PURE__ */ jsxs3("div", { className: "space-y-4", children: [
    /* @__PURE__ */ jsx4(Typography3, { as: "h2", variant: "body-L", strong: true, children: title }),
    /* @__PURE__ */ jsx4(
      motion2.ul,
      {
        className: "flex flex-wrap gap-2 overflow-hidden px-0.5",
        initial: false,
        animate: { height: showAll ? "auto" : maxVisible * 36 - 16 },
        transition: { duration: 0.3 },
        children: list.map((item) => /* @__PURE__ */ jsx4("li", { className: "flex justify-between", children: /* @__PURE__ */ jsxs3(
          ThemeButton_default,
          {
            theme: "secondary",
            className: "aria-pressed:border-tertiary bg-category shadow-category aria-[pressed=false]:border-category group flex shrink-0 items-center border-2 border-transparent transition-all",
            "aria-pressed": item.checked,
            onClick: (e) => {
              e.stopPropagation();
              e.preventDefault();
              onChange(item.value);
            },
            children: [
              /* @__PURE__ */ jsxs3("div", { className: "flex items-center gap-1", children: [
                item.icon && /* @__PURE__ */ jsx4(
                  ContentfulMedia_default,
                  {
                    media: item.icon,
                    width: 80,
                    height: 80,
                    className: "h-5 w-5",
                    format: null
                  }
                ),
                /* @__PURE__ */ jsxs3("div", { className: "space-y-0.5", children: [
                  /* @__PURE__ */ jsx4(Typography3, { variant: "label-L", children: item.title }),
                  item.checked && /* @__PURE__ */ jsx4(Divider, { className: "border-tertiary max-w-[40%] border" })
                ] })
              ] }),
              item.checked && /* @__PURE__ */ jsx4(CloseIcon, { className: "h-3 w-3 shrink-0" })
            ]
          }
        ) }, item.value))
      }
    ),
    list.length > maxVisible && /* @__PURE__ */ jsx4(
      Button,
      {
        type: "button",
        shape: "text",
        className: "p-0.5",
        onClick: () => {
          setShowAll((prev) => !prev);
        },
        children: /* @__PURE__ */ jsx4(Typography3, { variant: "link-S", children: showAll ? t("calendar_common:read_less") : t("calendar_common:read_more") })
      }
    )
  ] });
};
var CheckboxList_default = FilterCheckboxList;

// src/components/filters/Organizers.tsx
import { jsx as jsx5 } from "react/jsx-runtime";
var Organizers = () => {
  const { locale } = useRouter2();
  const { t } = useTranslation4("calendar_explore");
  const { data, isLoading } = useGetOrganizerList({
    lang: locale
  });
  const { setValue, watch } = useFormContext3();
  const value = watch("organizer") || [];
  return /* @__PURE__ */ jsx5(
    CheckboxList_default,
    {
      title: t("calendar_explore:season_organizer"),
      loading: isLoading,
      list: data == null ? void 0 : data.items.map((org) => ({
        title: org.title,
        value: org.slug,
        checked: value.includes(org.slug)
      })),
      onChange: (slug) => {
        const newValue = value.includes(slug) ? value.filter((v) => v !== slug) : value.concat(slug);
        setValue("organizer", newValue);
      }
    }
  );
};
var Organizers_default = Organizers;

// src/components/filters/categories/Selector.tsx
import { useFormContext as useFormContext4 } from "react-hook-form";
import { useRouter as useRouter3, useTranslation as useTranslation5 } from "@wbk/config";
import { jsx as jsx6 } from "react/jsx-runtime";
var CategoryFilterSelector = () => {
  const { locale } = useRouter3();
  const { t } = useTranslation5("calendar_explore");
  const { data, isLoading } = useGetCategories({
    limit: 40,
    lang: locale,
    where: { parent_exists: false }
  });
  const { setValue, watch } = useFormContext4();
  const value = watch("category") || "";
  return /* @__PURE__ */ jsx6(
    CheckboxList_default,
    {
      title: t("calendar_explore:categories"),
      loading: isLoading,
      list: data == null ? void 0 : data.map((cat) => ({
        title: cat.title,
        value: cat.slug,
        checked: value === cat.slug,
        icon: cat.icon
      })),
      onChange: (slug) => {
        setValue("category", slug);
      }
    }
  );
};
var Selector_default = CategoryFilterSelector;

// src/components/filters/Filters.tsx
import { jsx as jsx7, jsxs as jsxs4 } from "react/jsx-runtime";
var FILTERS = ["date", "price", "category", "organizer"];
var FILTERS_COMPONENTS = {
  date: /* @__PURE__ */ jsx7(Date_default, {}),
  price: /* @__PURE__ */ jsx7(Price_default, {}),
  category: /* @__PURE__ */ jsx7(Selector_default, {}),
  organizer: /* @__PURE__ */ jsx7(Organizers_default, {})
};
var FilterButton = ({ filters = FILTERS, showLabel = true }) => {
  const { t } = useTranslation6(["calendar_explore", "calendar_common"]);
  const { reset, total } = useFilterState();
  const modalRef = useRef(void 0);
  return /* @__PURE__ */ jsx7(
    ThemeResponsiveModal_default,
    {
      ignoreHistory: true,
      ref: modalRef,
      position: "right",
      actionsStyle: "fixed",
      className: "md:mx-4 md:mt-8 md:max-h-[calc(100vh-4rem)] md:p-4",
      title: t("calendar_explore:filters"),
      cancel: {
        label: t("calendar_common:clear_all"),
        className: "w-full",
        theme: "primary",
        onClick: () => {
          var _a;
          (_a = modalRef.current) == null ? void 0 : _a.close();
          reset();
        }
      },
      action: {
        label: t("calendar_common:apply"),
        form: "filters-form",
        className: "w-full",
        theme: "primary"
      },
      render: ({ open }) => /* @__PURE__ */ jsxs4(ThemeButton_default, { type: "button", theme: "secondary", onClick: open, children: [
        /* @__PURE__ */ jsx7(FilterIcon, { className: "h-4 w-4 shrink-0" }),
        showLabel && /* @__PURE__ */ jsx7(Typography4, { children: t("calendar_explore:filters") }),
        total > 0 && /* @__PURE__ */ jsx7("span", { className: "bg-error text-error-contrast flex h-5 w-5 items-center justify-center rounded-full text-xs", children: total })
      ] }),
      body: ({ close }) => /* @__PURE__ */ jsx7(Body, { filters, onClose: close })
    }
  );
};
var Body = ({ filters, onClose }) => {
  const { state, setState } = useFilterState();
  const methods = useForm({
    defaultValues: state
  });
  const onSubmit = (data) => {
    setState(data);
    onClose();
  };
  return /* @__PURE__ */ jsx7(FormProvider, __spreadProps(__spreadValues({}, methods), { children: /* @__PURE__ */ jsx7("form", { id: "filters-form", className: "space-y-6", onSubmit: methods.handleSubmit(onSubmit), children: filters == null ? void 0 : filters.map((filter, i) => /* @__PURE__ */ jsxs4(Fragment, { children: [
    FILTERS_COMPONENTS[filter],
    i < filters.length - 1 && /* @__PURE__ */ jsx7(Divider2, { className: "border-body-level-4" })
  ] }, filter)) }) }));
};
var Filters_default = FilterButton;

export {
  Filters_default
};
//# sourceMappingURL=chunk-CXILTCFP.mjs.map